// Write hooks for transactions.
//
// `useUpsertTransaction` wraps the insert-or-update wrapper; on success it
// invalidates the transactions list (and the by-ref entry) so reads refresh.
// Errors are NOT swallowed: onError surfaces a toast AND the hook still exposes
// `error`/`isError` for the caller. Invalidate-on-success is used (rather than
// an optimistic patch) because an upsert can change identity (insert) and touch
// many columns, so optimism would be riskier than a refetch.
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { queryKeys, invalidateRegister } from '../lib/queryKeys';
import { useToast } from '../components/ui/Toast';
import {
  upsertTransaction,
  softDeleteTransaction,
  restoreTransaction,
  type TransactionInsertInput,
  type AuditContext,
} from '../lib/dbWrite';

/**
 * Variables for {@link useUpsertTransaction}: the transaction payload plus the
 * OPTIONAL audit context (the caller-diffed note + from/to changes). Editors
 * compute the diff where the old row and the new input are both in hand and pass
 * it here; the import loop omits it (its per-row audit is suppressed).
 */
export interface UpsertTransactionVars {
  input: TransactionInsertInput;
  audit?: AuditContext;
}

/**
 * `opts.revive` (bulk import only) un-deletes a soft-deleted row that the upsert
 * resolves to — so a "replace population" re-upload resurfaces references purged
 * earlier (a no-op on live rows). Ordinary editors omit it, so a save can never
 * resurrect a deliberately purged transaction.
 *
 * `opts.importSource` (bulk import only, WS2 — 1 Jul 2026 incident) routes
 * updates through the import-scoped mapper — source columns only — so a
 * re-imported spreadsheet can never overwrite review/work-product state; the
 * revive also clears the row's removal-batch stamp. Ordinary editors omit it
 * and keep the full dual-write.
 */
export function useUpsertTransaction(opts: { revive?: boolean; importSource?: boolean } = {}) {
  const { show } = useToast();
  const revive = opts.revive === true;
  const importSource = opts.importSource === true;
  return useMutation({
    mutationFn: ({ input, audit }: UpsertTransactionVars) =>
      upsertTransaction(input, { revive, importSource, audit }),
    onSuccess: (result) => {
      // The whole register surface (incl. the reviewer-scoped key) — a transaction
      // edit must show on the working-paper view + scoped registers without a
      // hard refresh. The explicit by-ref key keeps the narrow contract visible.
      invalidateRegister(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.transactionByRef(result.referenceNo) });
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to save the transaction.', 'error');
    },
  });
}

/**
 * Soft-delete a transaction by `referenceNo` (admin-only at the call site). The
 * primary mutation flips the `deleted` flag and the wrapper records the
 * BEST-EFFORT `soft_delete_transaction` audit entry (server-stamped actor).
 * Invalidate-on-success so the register / manage list drops the row.
 */
export function useSoftDeleteTransaction() {
  const { show } = useToast();
  return useMutation({
    mutationFn: async (referenceNo: string) => {
      const id = await softDeleteTransaction(referenceNo);
      return { id, referenceNo };
    },
    onSuccess: (result) => {
      invalidateRegister(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.transactionByRef(result.referenceNo) });
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to delete the transaction.', 'error');
    },
  });
}

/**
 * Restore a removed (soft-deleted) transaction from the Manage screen (admin-only).
 * `fresh` also clears its existing findings so the record starts a clean cycle.
 * Invalidate-on-success so the manage list + register reflect the revived row.
 */
export function useRestoreTransaction() {
  const { show } = useToast();
  return useMutation({
    mutationFn: async ({ referenceNo, fresh }: { referenceNo: string; fresh: boolean }) => {
      const id = await restoreTransaction(referenceNo, { fresh });
      return { id, referenceNo };
    },
    onSuccess: (result) => {
      invalidateRegister(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.transactionByRef(result.referenceNo) });
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to restore the transaction.', 'error');
    },
  });
}
